import { useCallback, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { Upload, File as FileIcon } from 'lucide-react';

interface MultiFileUploadProps {
  accept?: Record<string, string[]>;
  maxSize?: number;
  onFilesSelect: (files: File[]) => void;
  disabled?: boolean;
  dropzoneText?: string;
  helperText?: string;
}

export function MultiFileUpload({
  accept = {
    'model/stl': ['.stl'],
    'application/sla': ['.stl'],
    'model/3mf': ['.3mf'],
    'application/vnd.ms-package.3dmanufacturing-3dmodel+xml': ['.3mf'],
  },
  maxSize = 100 * 1024 * 1024, // 100MB
  onFilesSelect,
  disabled = false,
  dropzoneText = 'Drop 3D model files here, or click to browse',
  helperText = 'STL and 3MF files, up to 100MB each',
}: MultiFileUploadProps) {
  const [error, setError] = useState<string | null>(null);
  const [draggedFiles, setDraggedFiles] = useState<File[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setError(null);
      setDraggedFiles([]);

      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        if (rejection.errors[0]?.code === 'file-too-large') {
          setError(`File is too large. Maximum size is ${Math.round(maxSize / 1024 / 1024)}MB.`);
        } else if (rejection.errors[0]?.code === 'file-invalid-type') {
          setError('Invalid file type. Only STL and 3MF files are accepted.');
        } else {
          setError(rejection.errors[0]?.message || 'Invalid file.');
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        onFilesSelect(acceptedFiles);
      }
    },
    [onFilesSelect, maxSize]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: true,
    disabled,
    onDragEnter: (e) => {
      // Show file count preview while dragging
      if (e.dataTransfer?.items) {
        const files = Array.from(e.dataTransfer.items)
          .filter((item) => item.kind === 'file')
          .map((item) => item.getAsFile())
          .filter((file): file is File => file !== null);
        setDraggedFiles(files);
      }
    },
    onDragLeave: () => {
      setDraggedFiles([]);
    },
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={`
          relative flex flex-col items-center justify-center
          border-2 border-dashed rounded-lg p-8
          transition-all cursor-pointer
          ${isDragActive ? 'border-primary bg-primary/10 scale-[1.02]' : 'border-border'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-border-hover hover:bg-background-subtle'}
          ${error ? 'border-error' : ''}
        `}
      >
        <input {...getInputProps()} />

        {isDragActive && draggedFiles.length > 0 ? (
          <div className="flex flex-col items-center gap-3">
            <div className="flex -space-x-2">
              {draggedFiles.slice(0, 5).map((_, index) => (
                <div
                  key={index}
                  className="w-12 h-12 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center"
                >
                  <FileIcon className="h-6 w-6 text-primary" />
                </div>
              ))}
              {draggedFiles.length > 5 && (
                <div className="w-12 h-12 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center">
                  <span className="text-xs font-semibold text-primary">+{draggedFiles.length - 5}</span>
                </div>
              )}
            </div>
            <p className="text-base font-semibold text-primary">
              Drop {draggedFiles.length} file{draggedFiles.length !== 1 ? 's' : ''} to add
            </p>
          </div>
        ) : (
          <>
            <Upload className="h-12 w-12 text-foreground-muted mb-4" />
            <p className="text-base font-medium text-foreground mb-1 text-center">
              {isDragActive ? 'Drop your files here' : dropzoneText}
            </p>
            <p className="text-sm text-foreground-muted text-center">{helperText}</p>
            {!isDragActive && (
              <p className="text-xs text-foreground-muted text-center mt-2">
                You can drop multiple files at once
              </p>
            )}
          </>
        )}
      </div>

      {error && <p className="text-sm text-error">{error}</p>}
    </div>
  );
}
